/**
 * SignalR Vuex Store Module
 * Manages SignalR connection state and real-time data
 * Enhanced with connection resilience monitoring
 */

import signalRService from '@/services/signalRService'

export default {
  namespaced: true,
  
  state: {
    // Connection status
    connectionStatus: 'disconnected',
    connectionStatusDetails: '',
    isConnected: false,
    connectionHealth: {
      isConnected: false,
      lastPing: null,
      lastPong: null,
      latency: null,
      connectionQuality: 'unknown'
    },
    resilience: {
      pageVisible: true,
      networkOnline: true,
      windowFocused: true,
      userActive: true,
      lastActivity: Date.now(),
      autoReconnect: true
    },
    
    // Real-time data
    notifications: [],
    unreadNotificationCount: 0,
    
    // Dashboard statistics
    dashboardStats: {
      employeeStats: null,
      attendanceStats: null,
      leaveStats: null,
      payrollStats: null,
      kpiStats: null,
      recruitmentStats: null,
      trainingStats: null
    },
    
    // Recruitment data
    recruitment: {
      manpowerRequests: [],
      jobAnnouncements: [],
      jobApplications: [],
      interviews: []
    },
    
    // Leave management data
    leave: {
      leaveRequests: [],
      leaveBalances: [],
      approvals: []
    },
    
    // Attendance data
    attendance: {
      checkIns: [],
      checkOuts: [],
      overtime: [],
      lateArrivals: [],
      earlyDepartures: [],
      absences: []
    },
    
    // Payroll data
    payroll: {
      salaryCalculations: [],
      payrollProcesses: [],
      deductions: [],
      bonuses: [],
      advances: []
    },
    
    // KPI & Performance data
    kpi: {
      evaluations: [],
      performanceReviews: [],
      incentives: []
    },
    
    // Training data
    training: {
      scheduledTrainings: [],
      completedTrainings: [],
      attendance: [],
      feedback: []
    },
    
    // Employee data
    employees: {
      profileUpdates: [],
      promotions: [],
      transfers: [],
      resignations: [],
      retirements: []
    },
    
    // Document data
    documents: {
      uploads: [],
      approvals: [],
      rejections: []
    },
    
    // System data
    system: {
      maintenance: [],
      backups: [],
      securityAlerts: []
    },
    
    // Approval workflow data
    approvals: {
      requests: [],
      assignments: [],
      decisions: []
    },
    
    // Reports data
    reports: {
      generated: [],
      exports: []
    },
    
    // Audit data
    audit: {
      logs: [],
      securityAlerts: []
    },
    
    // Event history for debugging
    eventHistory: [],
    
    // Loading states
    loading: {
      connecting: false,
      reconnecting: false
    },
    
    // Error handling
    error: null,
    lastError: null
  },
  
  mutations: {
    // Connection status mutations
    SET_CONNECTION_STATUS(state, { status, details }) {
      state.connectionStatus = status
      state.connectionStatusDetails = details
      state.isConnected = status === 'connected'
    },
    
    SET_CONNECTION_HEALTH(state, health) {
      state.connectionHealth = { ...state.connectionHealth, ...health }
    },
    
    SET_RESILIENCE_STATUS(state, resilience) {
      state.resilience = { ...state.resilience, ...resilience }
    },
    
    SET_LOADING(state, { type, loading }) {
      state.loading[type] = loading
    },
    
    SET_ERROR(state, error) {
      state.error = error
      state.lastError = {
        message: error?.message || 'Unknown error',
        timestamp: new Date().toISOString(),
        stack: error?.stack
      }
    },
    
    CLEAR_ERROR(state) {
      state.error = null
    },
    
    // Notification mutations
    ADD_NOTIFICATION(state, notification) {
      state.notifications.unshift(notification)
      if (!notification.isRead) {
        state.unreadNotificationCount++
      }
    },
    
    UPDATE_NOTIFICATION(state, notification) {
      const index = state.notifications.findIndex(n => n.id === notification.id)
      if (index !== -1) {
        state.notifications.splice(index, 1, notification)
      }
    },
    
    REMOVE_NOTIFICATION(state, notificationId) {
      const index = state.notifications.findIndex(n => n.id === notificationId)
      if (index !== -1) {
        const notification = state.notifications[index]
        if (!notification.isRead) {
          state.unreadNotificationCount = Math.max(0, state.unreadNotificationCount - 1)
        }
        state.notifications.splice(index, 1)
      }
    },
    
    MARK_NOTIFICATION_READ(state, notificationId) {
      const notification = state.notifications.find(n => n.id === notificationId)
      if (notification && !notification.isRead) {
        notification.isRead = true
        state.unreadNotificationCount = Math.max(0, state.unreadNotificationCount - 1)
      }
    },
    
    // Dashboard statistics mutations
    UPDATE_DASHBOARD_STATS(state, { type, data }) {
      state.dashboardStats[type] = data
    },
    
    // Recruitment mutations
    ADD_MANPOWER_REQUEST(state, request) {
      state.recruitment.manpowerRequests.unshift(request)
    },
    
    UPDATE_MANPOWER_REQUEST(state, request) {
      const index = state.recruitment.manpowerRequests.findIndex(r => r.id === request.id)
      if (index !== -1) {
        state.recruitment.manpowerRequests.splice(index, 1, request)
      }
    },
    
    ADD_JOB_ANNOUNCEMENT(state, announcement) {
      state.recruitment.jobAnnouncements.unshift(announcement)
    },
    
    ADD_JOB_APPLICATION(state, application) {
      state.recruitment.jobApplications.unshift(application)
    },
    
    UPDATE_JOB_APPLICATION(state, application) {
      const index = state.recruitment.jobApplications.findIndex(a => a.id === application.id)
      if (index !== -1) {
        state.recruitment.jobApplications.splice(index, 1, application)
      }
    },
    
    ADD_INTERVIEW(state, interview) {
      state.recruitment.interviews.unshift(interview)
    },
    
    UPDATE_INTERVIEW(state, interview) {
      const index = state.recruitment.interviews.findIndex(i => i.id === interview.id)
      if (index !== -1) {
        state.recruitment.interviews.splice(index, 1, interview)
      }
    },
    
    // Leave management mutations
    ADD_LEAVE_REQUEST(state, request) {
      state.leave.leaveRequests.unshift(request)
    },
    
    UPDATE_LEAVE_REQUEST(state, request) {
      const index = state.leave.leaveRequests.findIndex(r => r.id === request.id)
      if (index !== -1) {
        state.leave.leaveRequests.splice(index, 1, request)
      }
    },
    
    UPDATE_LEAVE_BALANCE(state, balance) {
      const index = state.leave.leaveBalances.findIndex(b => b.employeeId === balance.employeeId)
      if (index !== -1) {
        state.leave.leaveBalances.splice(index, 1, balance)
      } else {
        state.leave.leaveBalances.push(balance)
      }
    },
    
    // Attendance mutations
    ADD_CHECK_IN(state, checkIn) {
      state.attendance.checkIns.unshift(checkIn)
    },
    
    ADD_CHECK_OUT(state, checkOut) {
      state.attendance.checkOuts.unshift(checkOut)
    },
    
    ADD_OVERTIME(state, overtime) {
      state.attendance.overtime.unshift(overtime)
    },
    
    ADD_LATE_ARRIVAL(state, lateArrival) {
      state.attendance.lateArrivals.unshift(lateArrival)
    },
    
    ADD_EARLY_DEPARTURE(state, earlyDeparture) {
      state.attendance.earlyDepartures.unshift(earlyDeparture)
    },
    
    ADD_ABSENCE(state, absence) {
      state.attendance.absences.unshift(absence)
    },
    
    // Payroll mutations
    ADD_SALARY_CALCULATION(state, calculation) {
      state.payroll.salaryCalculations.unshift(calculation)
    },
    
    ADD_PAYROLL_PROCESS(state, process) {
      state.payroll.payrollProcesses.unshift(process)
    },
    
    ADD_DEDUCTION(state, deduction) {
      state.payroll.deductions.unshift(deduction)
    },
    
    ADD_BONUS(state, bonus) {
      state.payroll.bonuses.unshift(bonus)
    },
    
    ADD_ADVANCE(state, advance) {
      state.payroll.advances.unshift(advance)
    },
    
    // KPI & Performance mutations
    ADD_KPI_EVALUATION(state, evaluation) {
      state.kpi.evaluations.unshift(evaluation)
    },
    
    UPDATE_KPI_EVALUATION(state, evaluation) {
      const index = state.kpi.evaluations.findIndex(e => e.id === evaluation.id)
      if (index !== -1) {
        state.kpi.evaluations.splice(index, 1, evaluation)
      }
    },
    
    ADD_PERFORMANCE_REVIEW(state, review) {
      state.kpi.performanceReviews.unshift(review)
    },
    
    ADD_INCENTIVE(state, incentive) {
      state.kpi.incentives.unshift(incentive)
    },
    
    // Training mutations
    ADD_TRAINING_SCHEDULED(state, training) {
      state.training.scheduledTrainings.unshift(training)
    },
    
    ADD_TRAINING_COMPLETED(state, training) {
      state.training.completedTrainings.unshift(training)
    },
    
    ADD_TRAINING_ATTENDANCE(state, attendance) {
      state.training.attendance.unshift(attendance)
    },
    
    ADD_TRAINING_FEEDBACK(state, feedback) {
      state.training.feedback.unshift(feedback)
    },
    
    // Employee mutations
    ADD_EMPLOYEE_PROFILE_UPDATE(state, update) {
      state.employees.profileUpdates.unshift(update)
    },
    
    ADD_EMPLOYEE_PROMOTION(state, promotion) {
      state.employees.promotions.unshift(promotion)
    },
    
    ADD_EMPLOYEE_TRANSFER(state, transfer) {
      state.employees.transfers.unshift(transfer)
    },
    
    ADD_EMPLOYEE_RESIGNATION(state, resignation) {
      state.employees.resignations.unshift(resignation)
    },
    
    ADD_EMPLOYEE_RETIREMENT(state, retirement) {
      state.employees.retirements.unshift(retirement)
    },
    
    // Document mutations
    ADD_DOCUMENT_UPLOAD(state, document) {
      state.documents.uploads.unshift(document)
    },
    
    ADD_DOCUMENT_APPROVAL(state, document) {
      state.documents.approvals.unshift(document)
    },
    
    ADD_DOCUMENT_REJECTION(state, document) {
      state.documents.rejections.unshift(document)
    },
    
    // System mutations
    ADD_SYSTEM_MAINTENANCE(state, maintenance) {
      state.system.maintenance.unshift(maintenance)
    },
    
    ADD_SYSTEM_BACKUP(state, backup) {
      state.system.backups.unshift(backup)
    },
    
    ADD_SECURITY_ALERT(state, alert) {
      state.system.securityAlerts.unshift(alert)
    },
    
    // Approval workflow mutations
    ADD_APPROVAL_REQUEST(state, request) {
      state.approvals.requests.unshift(request)
    },
    
    UPDATE_APPROVAL_REQUEST(state, request) {
      const index = state.approvals.requests.findIndex(r => r.id === request.id)
      if (index !== -1) {
        state.approvals.requests.splice(index, 1, request)
      }
    },
    
    ADD_APPROVAL_ASSIGNMENT(state, assignment) {
      state.approvals.assignments.unshift(assignment)
    },
    
    ADD_APPROVAL_DECISION(state, decision) {
      state.approvals.decisions.unshift(decision)
    },
    
    // Report mutations
    ADD_REPORT_GENERATED(state, report) {
      state.reports.generated.unshift(report)
    },
    
    ADD_REPORT_EXPORT(state, report) {
      state.reports.exports.unshift(report)
    },
    
    // Audit mutations
    ADD_AUDIT_LOG(state, log) {
      state.audit.logs.unshift(log)
    },
    
    ADD_AUDIT_SECURITY_ALERT(state, alert) {
      state.audit.securityAlerts.unshift(alert)
    },
    
    // Event history mutations
    ADD_EVENT_TO_HISTORY(state, event) {
      state.eventHistory.unshift({
        ...event,
        timestamp: new Date().toISOString()
      })
      
      // Keep only last 100 events
      if (state.eventHistory.length > 100) {
        state.eventHistory = state.eventHistory.slice(0, 100)
      }
    }
  },
  
  actions: {
    // Connection actions
    async connect({ commit, dispatch }) {
      commit('SET_LOADING', { type: 'connecting', loading: true })
      commit('CLEAR_ERROR')
      
      try {
        await signalRService.connect()
        
        // Set up event listeners
        dispatch('setupEventListeners')
        
        // Update connection status
        const status = signalRService.getConnectionStatus()
        commit('SET_CONNECTION_STATUS', {
          status: status.status,
          details: status.details
        })
        
        commit('SET_CONNECTION_HEALTH', status.connectionHealth)
        commit('SET_RESILIENCE_STATUS', status.resilience)
        
      } catch (error) {
        commit('SET_ERROR', error)
        console.error('Failed to connect to SignalR:', error)
      } finally {
        commit('SET_LOADING', { type: 'connecting', loading: false })
      }
    },
    
    async disconnect({ commit }) {
      try {
        await signalRService.disconnect()
        commit('SET_CONNECTION_STATUS', { status: 'disconnected', details: 'Disconnected' })
      } catch (error) {
        commit('SET_ERROR', error)
        console.error('Failed to disconnect from SignalR:', error)
      }
    },
    
    async forceReconnect({ commit, dispatch }) {
      commit('SET_LOADING', { type: 'reconnecting', loading: true })
      commit('CLEAR_ERROR')
      
      try {
        await signalRService.forceReconnect()
        
        // Re-setup event listeners
        dispatch('setupEventListeners')
        
        const status = signalRService.getConnectionStatus()
        commit('SET_CONNECTION_STATUS', {
          status: status.status,
          details: status.details
        })
        
      } catch (error) {
        commit('SET_ERROR', error)
        console.error('Failed to force reconnect:', error)
      } finally {
        commit('SET_LOADING', { type: 'reconnecting', loading: false })
      }
    },
    
    // Set up all event listeners
    setupEventListeners({ commit }) {
      // Notification events
      signalRService.onGlobal('notification', (notification) => {
        commit('ADD_NOTIFICATION', notification)
        commit('ADD_EVENT_TO_HISTORY', { type: 'notification', data: notification })
      })
      
      signalRService.onGlobal('notificationUpdated', (notification) => {
        commit('UPDATE_NOTIFICATION', notification)
        commit('ADD_EVENT_TO_HISTORY', { type: 'notificationUpdated', data: notification })
      })
      
      signalRService.onGlobal('notificationDeleted', (notificationId) => {
        commit('REMOVE_NOTIFICATION', notificationId)
        commit('ADD_EVENT_TO_HISTORY', { type: 'notificationDeleted', data: notificationId })
      })
      
      // Dashboard statistics events
      signalRService.onGlobal('dashboardStatsUpdated', (stats) => {
        commit('UPDATE_DASHBOARD_STATS', { type: 'dashboardStats', data: stats })
        commit('ADD_EVENT_TO_HISTORY', { type: 'dashboardStatsUpdated', data: stats })
      })
      
      signalRService.onGlobal('employeeStatsUpdated', (stats) => {
        commit('UPDATE_DASHBOARD_STATS', { type: 'employeeStats', data: stats })
        commit('ADD_EVENT_TO_HISTORY', { type: 'employeeStatsUpdated', data: stats })
      })
      
      signalRService.onGlobal('attendanceStatsUpdated', (stats) => {
        commit('UPDATE_DASHBOARD_STATS', { type: 'attendanceStats', data: stats })
        commit('ADD_EVENT_TO_HISTORY', { type: 'attendanceStatsUpdated', data: stats })
      })
      
      signalRService.onGlobal('leaveStatsUpdated', (stats) => {
        commit('UPDATE_DASHBOARD_STATS', { type: 'leaveStats', data: stats })
        commit('ADD_EVENT_TO_HISTORY', { type: 'leaveStatsUpdated', data: stats })
      })
      
      signalRService.onGlobal('payrollStatsUpdated', (stats) => {
        commit('UPDATE_DASHBOARD_STATS', { type: 'payrollStats', data: stats })
        commit('ADD_EVENT_TO_HISTORY', { type: 'payrollStatsUpdated', data: stats })
      })
      
      signalRService.onGlobal('kpiStatsUpdated', (stats) => {
        commit('UPDATE_DASHBOARD_STATS', { type: 'kpiStats', data: stats })
        commit('ADD_EVENT_TO_HISTORY', { type: 'kpiStatsUpdated', data: stats })
      })
      
      signalRService.onGlobal('recruitmentStatsUpdated', (stats) => {
        commit('UPDATE_DASHBOARD_STATS', { type: 'recruitmentStats', data: stats })
        commit('ADD_EVENT_TO_HISTORY', { type: 'recruitmentStatsUpdated', data: stats })
      })
      
      // Recruitment events
      signalRService.onGlobal('manpowerRequestCreated', (request) => {
        commit('ADD_MANPOWER_REQUEST', request)
        commit('ADD_EVENT_TO_HISTORY', { type: 'manpowerRequestCreated', data: request })
      })
      
      signalRService.onGlobal('manpowerRequestUpdated', (request) => {
        commit('UPDATE_MANPOWER_REQUEST', request)
        commit('ADD_EVENT_TO_HISTORY', { type: 'manpowerRequestUpdated', data: request })
      })
      
      signalRService.onGlobal('jobAnnouncementPublished', (announcement) => {
        commit('ADD_JOB_ANNOUNCEMENT', announcement)
        commit('ADD_EVENT_TO_HISTORY', { type: 'jobAnnouncementPublished', data: announcement })
      })
      
      signalRService.onGlobal('jobApplicationReceived', (application) => {
        commit('ADD_JOB_APPLICATION', application)
        commit('ADD_EVENT_TO_HISTORY', { type: 'jobApplicationReceived', data: application })
      })
      
      signalRService.onGlobal('jobApplicationStatusChanged', (application) => {
        commit('UPDATE_JOB_APPLICATION', application)
        commit('ADD_EVENT_TO_HISTORY', { type: 'jobApplicationStatusChanged', data: application })
      })
      
      signalRService.onGlobal('interviewScheduled', (interview) => {
        commit('ADD_INTERVIEW', interview)
        commit('ADD_EVENT_TO_HISTORY', { type: 'interviewScheduled', data: interview })
      })
      
      signalRService.onGlobal('interviewCompleted', (interview) => {
        commit('UPDATE_INTERVIEW', interview)
        commit('ADD_EVENT_TO_HISTORY', { type: 'interviewCompleted', data: interview })
      })
      
      // Leave management events
      signalRService.onGlobal('leaveRequestSubmitted', (request) => {
        commit('ADD_LEAVE_REQUEST', request)
        commit('ADD_EVENT_TO_HISTORY', { type: 'leaveRequestSubmitted', data: request })
      })
      
      signalRService.onGlobal('leaveRequestApproved', (request) => {
        commit('UPDATE_LEAVE_REQUEST', request)
        commit('ADD_EVENT_TO_HISTORY', { type: 'leaveRequestApproved', data: request })
      })
      
      signalRService.onGlobal('leaveRequestRejected', (request) => {
        commit('UPDATE_LEAVE_REQUEST', request)
        commit('ADD_EVENT_TO_HISTORY', { type: 'leaveRequestRejected', data: request })
      })
      
      signalRService.onGlobal('leaveBalanceUpdated', (balance) => {
        commit('UPDATE_LEAVE_BALANCE', balance)
        commit('ADD_EVENT_TO_HISTORY', { type: 'leaveBalanceUpdated', data: balance })
      })
      
      // Attendance events
      signalRService.onGlobal('checkInRecorded', (checkIn) => {
        commit('ADD_CHECK_IN', checkIn)
        commit('ADD_EVENT_TO_HISTORY', { type: 'checkInRecorded', data: checkIn })
      })
      
      signalRService.onGlobal('checkOutRecorded', (checkOut) => {
        commit('ADD_CHECK_OUT', checkOut)
        commit('ADD_EVENT_TO_HISTORY', { type: 'checkOutRecorded', data: checkOut })
      })
      
      signalRService.onGlobal('overtimeCalculated', (overtime) => {
        commit('ADD_OVERTIME', overtime)
        commit('ADD_EVENT_TO_HISTORY', { type: 'overtimeCalculated', data: overtime })
      })
      
      signalRService.onGlobal('lateArrivalDetected', (lateArrival) => {
        commit('ADD_LATE_ARRIVAL', lateArrival)
        commit('ADD_EVENT_TO_HISTORY', { type: 'lateArrivalDetected', data: lateArrival })
      })
      
      signalRService.onGlobal('earlyDepartureDetected', (earlyDeparture) => {
        commit('ADD_EARLY_DEPARTURE', earlyDeparture)
        commit('ADD_EVENT_TO_HISTORY', { type: 'earlyDepartureDetected', data: earlyDeparture })
      })
      
      signalRService.onGlobal('absenceDetected', (absence) => {
        commit('ADD_ABSENCE', absence)
        commit('ADD_EVENT_TO_HISTORY', { type: 'absenceDetected', data: absence })
      })
      
      // Payroll events
      signalRService.onGlobal('salaryCalculated', (calculation) => {
        commit('ADD_SALARY_CALCULATION', calculation)
        commit('ADD_EVENT_TO_HISTORY', { type: 'salaryCalculated', data: calculation })
      })
      
      signalRService.onGlobal('payrollProcessed', (process) => {
        commit('ADD_PAYROLL_PROCESS', process)
        commit('ADD_EVENT_TO_HISTORY', { type: 'payrollProcessed', data: process })
      })
      
      signalRService.onGlobal('deductionApplied', (deduction) => {
        commit('ADD_DEDUCTION', deduction)
        commit('ADD_EVENT_TO_HISTORY', { type: 'deductionApplied', data: deduction })
      })
      
      signalRService.onGlobal('bonusAwarded', (bonus) => {
        commit('ADD_BONUS', bonus)
        commit('ADD_EVENT_TO_HISTORY', { type: 'bonusAwarded', data: bonus })
      })
      
      signalRService.onGlobal('advanceRequested', (advance) => {
        commit('ADD_ADVANCE', advance)
        commit('ADD_EVENT_TO_HISTORY', { type: 'advanceRequested', data: advance })
      })
      
      // KPI & Performance events
      signalRService.onGlobal('kpiEvaluationSubmitted', (evaluation) => {
        commit('ADD_KPI_EVALUATION', evaluation)
        commit('ADD_EVENT_TO_HISTORY', { type: 'kpiEvaluationSubmitted', data: evaluation })
      })
      
      signalRService.onGlobal('kpiEvaluationApproved', (evaluation) => {
        commit('UPDATE_KPI_EVALUATION', evaluation)
        commit('ADD_EVENT_TO_HISTORY', { type: 'kpiEvaluationApproved', data: evaluation })
      })
      
      signalRService.onGlobal('performanceReviewScheduled', (review) => {
        commit('ADD_PERFORMANCE_REVIEW', review)
        commit('ADD_EVENT_TO_HISTORY', { type: 'performanceReviewScheduled', data: review })
      })
      
      signalRService.onGlobal('incentiveCalculated', (incentive) => {
        commit('ADD_INCENTIVE', incentive)
        commit('ADD_EVENT_TO_HISTORY', { type: 'incentiveCalculated', data: incentive })
      })
      
      // Training events
      signalRService.onGlobal('trainingScheduled', (training) => {
        commit('ADD_TRAINING_SCHEDULED', training)
        commit('ADD_EVENT_TO_HISTORY', { type: 'trainingScheduled', data: training })
      })
      
      signalRService.onGlobal('trainingCompleted', (training) => {
        commit('ADD_TRAINING_COMPLETED', training)
        commit('ADD_EVENT_TO_HISTORY', { type: 'trainingCompleted', data: training })
      })
      
      signalRService.onGlobal('trainingAttendanceRecorded', (attendance) => {
        commit('ADD_TRAINING_ATTENDANCE', attendance)
        commit('ADD_EVENT_TO_HISTORY', { type: 'trainingAttendanceRecorded', data: attendance })
      })
      
      signalRService.onGlobal('trainingFeedbackSubmitted', (feedback) => {
        commit('ADD_TRAINING_FEEDBACK', feedback)
        commit('ADD_EVENT_TO_HISTORY', { type: 'trainingFeedbackSubmitted', data: feedback })
      })
      
      // Employee events
      signalRService.onGlobal('employeeProfileUpdated', (update) => {
        commit('ADD_EMPLOYEE_PROFILE_UPDATE', update)
        commit('ADD_EVENT_TO_HISTORY', { type: 'employeeProfileUpdated', data: update })
      })
      
      signalRService.onGlobal('employeePromoted', (promotion) => {
        commit('ADD_EMPLOYEE_PROMOTION', promotion)
        commit('ADD_EVENT_TO_HISTORY', { type: 'employeePromoted', data: promotion })
      })
      
      signalRService.onGlobal('employeeTransferred', (transfer) => {
        commit('ADD_EMPLOYEE_TRANSFER', transfer)
        commit('ADD_EVENT_TO_HISTORY', { type: 'employeeTransferred', data: transfer })
      })
      
      signalRService.onGlobal('employeeResigned', (resignation) => {
        commit('ADD_EMPLOYEE_RESIGNATION', resignation)
        commit('ADD_EVENT_TO_HISTORY', { type: 'employeeResigned', data: resignation })
      })
      
      // Document events
      signalRService.onGlobal('documentUploaded', (document) => {
        commit('ADD_DOCUMENT_UPLOAD', document)
        commit('ADD_EVENT_TO_HISTORY', { type: 'documentUploaded', data: document })
      })
      
      signalRService.onGlobal('documentApproved', (document) => {
        commit('ADD_DOCUMENT_APPROVAL', document)
        commit('ADD_EVENT_TO_HISTORY', { type: 'documentApproved', data: document })
      })
      
      signalRService.onGlobal('documentRejected', (document) => {
        commit('ADD_DOCUMENT_REJECTION', document)
        commit('ADD_EVENT_TO_HISTORY', { type: 'documentRejected', data: document })
      })
      
      // System events
      signalRService.onGlobal('systemMaintenance', (maintenance) => {
        commit('ADD_SYSTEM_MAINTENANCE', maintenance)
        commit('ADD_EVENT_TO_HISTORY', { type: 'systemMaintenance', data: maintenance })
      })
      
      signalRService.onGlobal('dataBackupCompleted', (backup) => {
        commit('ADD_SYSTEM_BACKUP', backup)
        commit('ADD_EVENT_TO_HISTORY', { type: 'dataBackupCompleted', data: backup })
      })
      
      signalRService.onGlobal('securityAlert', (alert) => {
        commit('ADD_SECURITY_ALERT', alert)
        commit('ADD_EVENT_TO_HISTORY', { type: 'securityAlert', data: alert })
      })
      
      // Approval workflow events
      signalRService.onGlobal('approvalRequestSubmitted', (request) => {
        commit('ADD_APPROVAL_REQUEST', request)
        commit('ADD_EVENT_TO_HISTORY', { type: 'approvalRequestSubmitted', data: request })
      })
      
      signalRService.onGlobal('approvalRequestAssigned', (assignment) => {
        commit('ADD_APPROVAL_ASSIGNMENT', assignment)
        commit('ADD_EVENT_TO_HISTORY', { type: 'approvalRequestAssigned', data: assignment })
      })
      
      signalRService.onGlobal('approvalRequestApproved', (decision) => {
        commit('ADD_APPROVAL_DECISION', decision)
        commit('ADD_EVENT_TO_HISTORY', { type: 'approvalRequestApproved', data: decision })
      })
      
      signalRService.onGlobal('approvalRequestRejected', (decision) => {
        commit('ADD_APPROVAL_DECISION', decision)
        commit('ADD_EVENT_TO_HISTORY', { type: 'approvalRequestRejected', data: decision })
      })
      
      // Report events
      signalRService.onGlobal('reportGenerated', (report) => {
        commit('ADD_REPORT_GENERATED', report)
        commit('ADD_EVENT_TO_HISTORY', { type: 'reportGenerated', data: report })
      })
      
      signalRService.onGlobal('reportExportCompleted', (report) => {
        commit('ADD_REPORT_EXPORT', report)
        commit('ADD_EVENT_TO_HISTORY', { type: 'reportExportCompleted', data: report })
      })
      
      // Audit events
      signalRService.onGlobal('auditLogCreated', (log) => {
        commit('ADD_AUDIT_LOG', log)
        commit('ADD_EVENT_TO_HISTORY', { type: 'auditLogCreated', data: log })
      })
      
      signalRService.onGlobal('securityAlert', (alert) => {
        commit('ADD_AUDIT_SECURITY_ALERT', alert)
        commit('ADD_EVENT_TO_HISTORY', { type: 'securityAlert', data: alert })
      })
    },
    
    // Utility actions
    markNotificationAsRead({ commit }, notificationId) {
      commit('MARK_NOTIFICATION_READ', notificationId)
    },
    
    setAutoReconnect({ commit }, enabled) {
      signalRService.setAutoReconnect(enabled)
      commit('SET_RESILIENCE_STATUS', { autoReconnect: enabled })
    },
    
    clearEventHistory({ commit }) {
      commit('SET_EVENT_HISTORY', [])
    },
    
    clearError({ commit }) {
      commit('CLEAR_ERROR')
    }
  },
  
  getters: {
    // Connection getters
    isConnected: state => state.isConnected,
    connectionStatus: state => state.connectionStatus,
    connectionStatusDetails: state => state.connectionStatusDetails,
    connectionHealth: state => state.connectionHealth,
    resilience: state => state.resilience,
    isLoading: state => state.loading.connecting || state.loading.reconnecting,
    error: state => state.error,
    lastError: state => state.lastError,
    
    // Notification getters
    notifications: state => state.notifications,
    unreadNotificationCount: state => state.unreadNotificationCount,
    recentNotifications: state => state.notifications.slice(0, 10),
    
    // Dashboard statistics getters
    dashboardStats: state => state.dashboardStats,
    employeeStats: state => state.dashboardStats.employeeStats,
    attendanceStats: state => state.dashboardStats.attendanceStats,
    leaveStats: state => state.dashboardStats.leaveStats,
    payrollStats: state => state.dashboardStats.payrollStats,
    kpiStats: state => state.dashboardStats.kpiStats,
    recruitmentStats: state => state.dashboardStats.recruitmentStats,
    
    // Recruitment getters
    recruitment: state => state.recruitment,
    manpowerRequests: state => state.recruitment.manpowerRequests,
    jobAnnouncements: state => state.recruitment.jobAnnouncements,
    jobApplications: state => state.recruitment.jobApplications,
    interviews: state => state.recruitment.interviews,
    
    // Leave management getters
    leave: state => state.leave,
    leaveRequests: state => state.leave.leaveRequests,
    leaveBalances: state => state.leave.leaveBalances,
    
    // Attendance getters
    attendance: state => state.attendance,
    checkIns: state => state.attendance.checkIns,
    checkOuts: state => state.attendance.checkOuts,
    overtime: state => state.attendance.overtime,
    lateArrivals: state => state.attendance.lateArrivals,
    earlyDepartures: state => state.attendance.earlyDepartures,
    absences: state => state.attendance.absences,
    
    // Payroll getters
    payroll: state => state.payroll,
    salaryCalculations: state => state.payroll.salaryCalculations,
    payrollProcesses: state => state.payroll.payrollProcesses,
    deductions: state => state.payroll.deductions,
    bonuses: state => state.payroll.bonuses,
    advances: state => state.payroll.advances,
    
    // KPI & Performance getters
    kpi: state => state.kpi,
    kpiEvaluations: state => state.kpi.evaluations,
    performanceReviews: state => state.kpi.performanceReviews,
    incentives: state => state.kpi.incentives,
    
    // Training getters
    training: state => state.training,
    scheduledTrainings: state => state.training.scheduledTrainings,
    completedTrainings: state => state.training.completedTrainings,
    trainingAttendance: state => state.training.attendance,
    trainingFeedback: state => state.training.feedback,
    
    // Employee getters
    employees: state => state.employees,
    employeeProfileUpdates: state => state.employees.profileUpdates,
    employeePromotions: state => state.employees.promotions,
    employeeTransfers: state => state.employees.transfers,
    employeeResignations: state => state.employees.resignations,
    employeeRetirements: state => state.employees.retirements,
    
    // Document getters
    documents: state => state.documents,
    documentUploads: state => state.documents.uploads,
    documentApprovals: state => state.documents.approvals,
    documentRejections: state => state.documents.rejections,
    
    // System getters
    system: state => state.system,
    systemMaintenance: state => state.system.maintenance,
    systemBackups: state => state.system.backups,
    securityAlerts: state => state.system.securityAlerts,
    
    // Approval workflow getters
    approvals: state => state.approvals,
    approvalRequests: state => state.approvals.requests,
    approvalAssignments: state => state.approvals.assignments,
    approvalDecisions: state => state.approvals.decisions,
    
    // Report getters
    reports: state => state.reports,
    generatedReports: state => state.reports.generated,
    exportedReports: state => state.reports.exports,
    
    // Audit getters
    audit: state => state.audit,
    auditLogs: state => state.audit.logs,
    auditSecurityAlerts: state => state.audit.securityAlerts,
    
    // Event history getters
    eventHistory: state => state.eventHistory,
    recentEvents: state => state.eventHistory.slice(0, 20),
    
    // Computed getters
    hasActiveNotifications: state => state.unreadNotificationCount > 0,
    hasRecentActivity: state => state.eventHistory.length > 0,
    connectionQuality: state => state.connectionHealth.connectionQuality,
    isConnectionHealthy: state => state.connectionHealth.connectionQuality !== 'poor',
    
    // Filtered getters
    pendingApprovals: state => state.approvals.requests.filter(r => r.status === 'pending'),
    urgentNotifications: state => state.notifications.filter(n => n.priority >= 3),
    recentCheckIns: state => state.attendance.checkIns.slice(0, 5),
    recentLeaveRequests: state => state.leave.leaveRequests.slice(0, 5),
    recentKpiEvaluations: state => state.kpi.evaluations.slice(0, 5)
  }
} 